import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * User roles matching backend UserRole enum
 */
export type UserRole = 'SUPER_ADMIN' | 'ADMIN' | 'USER';

/**
 * User status matching backend UserStatus enum
 */
export type UserStatus = 'ACTIVE' | 'INACTIVE';

/**
 * User interface matching backend UserDto
 */
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  status?: UserStatus;
  language?: 'en' | 'fr';
  permissions?: string[];
}

/**
 * Auth state interface
 */
interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isHydrated: boolean;

  // Actions
  login: (user: User, accessToken: string, refreshToken: string) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  updateUser: (updates: Partial<User>| User) => void;
  logout: () => void;
  clearAuth: () => void;
  setHydrated: (state: boolean) => void;
}

/**
 * Auth store with persistence to localStorage
 * 
 * Features:
 * - Persists user session across page reloads
 * - Handles token rotation on refresh
 * - Hydration tracking for SSR compatibility
 * - Role-based access control helpers
 */
// src/store/useAuthStore.ts

// 1. Définir l'état initial séparément pour verrouiller les types
const initialState = {
  user: null as User | null,
  accessToken: null as string | null,
  refreshToken: null as string | null,
  isAuthenticated: false,
  isHydrated: false,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...initialState,

      login: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : (updates as User),
        })),

      logout: () => set({ ...initialState, isHydrated: true }),

      clearAuth: () => set({ ...initialState, isHydrated: true }),

      setHydrated: (state) => set({ isHydrated: state }),
    }),
    {
      name: 'yim-auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated(true);
      },
    }
  )
);

/**
 * Selector hooks for common use cases
 */
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAccessToken = () => useAuthStore((state) => state.accessToken);
export const useIsHydrated = () => useAuthStore((state) => state.isHydrated);

/**
 * Role-based access control selectors
 */
export const useIsSuperAdmin = () => 
  useAuthStore((state) => state.user?.role === 'SUPER_ADMIN');

export const useIsAdmin = () => 
  useAuthStore((state) => 
    state.user?.role === 'SUPER_ADMIN' || state.user?.role === 'ADMIN'
  );

export const useUserRole = () => 
  useAuthStore((state) => state.user?.role);

/**
 * Check if user has at least the given role (considering hierarchy)
 * SUPER_ADMIN > ADMIN > USER
 */
export const useHasRole = (requiredRole: UserRole) => 
  useAuthStore((state) => {
    const userRole = state.user?.role;
    if (!userRole) return false;
    
    const roleHierarchy: Record<UserRole, number> = {
      SUPER_ADMIN: 3,
      ADMIN: 2,
      USER: 1,
    };
    
    return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
  });

/**
 * Get auth state outside of React components
 * Useful for API interceptors
 */
export const getAuthState = () => useAuthStore.getState();

/**
 * Check role outside of React components
 */
export const isSuperAdmin = () => getAuthState().user?.role === 'SUPER_ADMIN';
export const isAdmin = () => {
  const role = getAuthState().user?.role;
  return role === 'SUPER_ADMIN' || role === 'ADMIN';
};
